import React from "react";
import { Box, type Breakpoint, Skeleton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DotLegalHeading } from "@dotlegal/dotlegal-ui-components";
import { contentResponsiveSize, useContentSize } from "../../../main/content/ContentSizeContext";

// Every top-bar tile shares this one responsive width so tiles stay uniformly sized and aligned across rows and pages.
// Responsiveness is owned by the kit, not the call site — pages cannot vary it.
const topBarTileColumns: Partial<Record<Breakpoint, number>> = { xs: 6, sm: 4, md: 4, lg: 2, xl: 2 };

// At the widest breakpoints a tile takes 2 of the grid's 12 columns, so a row holds six. Overview pages use this to
// decide whether their tiles fit in a single row; narrower screens wrap those same six onto more lines.
export const tilesInOneRow = 6;

// Resolves against the *content area* width (via useContentSize) rather than the viewport, so tiles react when the
// nav menu or AI chat is open and the available width shrinks. Exposed so the rare label-less raw <Grid> tile can
// share the exact same width.
export function useTopBarTileSize(): number {
    const { breakpoint } = useContentSize();
    return contentResponsiveSize(breakpoint, topBarTileColumns);
}

export interface ITopBarTileProps {
    label: string;
    isLoading?: boolean;
    children?: React.ReactNode;
}

// The content slot keeps a consistent height across loading and loaded states so the row doesn't shift on load.
function TopBarTile(props: ITopBarTileProps) {
    const theme = useTheme();
    const size = useTopBarTileSize();
    return (
        <Grid size={size}>
            <DotLegalHeading name={props.label}>
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", minHeight: theme.spacing(5) }}>
                    {props.isLoading ? <Skeleton variant="text" sx={{ marginX: "auto", width: theme.spacing(8) }} /> : props.children}
                </Box>
            </DotLegalHeading>
        </Grid>
    );
}

export default TopBarTile;
